/* ============================================================
   POUT POUCH — BRIDAL LANDING PAGE JS  v3
   Upload to: assets/bridal-landing.js
   
   Changes in v3:
   - Swatch buttons use actual product images (not CSS gradients)
   - Summary shows product image thumbnails
   - Bridesmaid slots are renumbered after every add/remove
     so the sequence is always 1, 2, 3... with no gaps
   - Free shipping threshold read from window.BRIDAL_PRODUCTS
   - Charms removed from builder (products no longer stocked)
   ============================================================ */

(function () {
  'use strict';

  /* ── STATE ──────────────────────────────────────────────── */
  var slots    = [];   // current slot array
  var products = null; // from window.BRIDAL_PRODUCTS
  var FREE_SHIP_AUD = 75; // default — overridden by Liquid
  var slotUid   = 0;   // ever-incrementing uid for DOM keys

  /* ── HELPERS ─────────────────────────────────────────────── */
  function fmtAUD(cents) {
    return '$' + (cents / 100).toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  function esc(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function getPouchByColour(colour) {
    return products.pouches.find(function (p) { return p.colour === colour; }) || null;
  }

  /* Returns the discount % that applies for a given pouch quantity (0 if none) */
  function getDiscountPct(qty) {
    var tiers = products.discountTiers || [];
    var pct = 0;
    tiers.forEach(function (t) { if (qty >= t.minQty && t.pct > 0) pct = t.pct; });
    return pct;
  }

  /* Returns the next tier the customer hasn't reached yet, or null if at max */
  function getNextDiscountTier(qty) {
    var tiers = products.discountTiers || [];
    for (var i = 0; i < tiers.length; i++) {
      if (qty < tiers[i].minQty && tiers[i].pct > 0) return tiers[i];
    }
    return null;
  }

  /* ── RENUMBER BRIDESMAID LABELS ──────────────────────────
     Runs after every add or remove.
     Goes through the slots array in order and re-assigns
     sequential numbers to maid/extra roles so there are
     never gaps (1, 2, 4... becomes 1, 2, 3).
  ───────────────────────────────────────────────────────── */
  function renumberSlots() {
    var maidNum  = 0;
    var extraNum = 0;

    slots.forEach(function (slot) {
      if (slot.role === 'maid') {
        maidNum++;
        slot.label = 'Bridesmaid ' + maidNum;
      } else if (slot.role === 'extra') {
        extraNum++;
        slot.label = extraNum === 1 ? 'Mum / Flower Girl' : 'Extra ' + extraNum;
      }
    });
  }

  /* ── BUILD CHARM ROW HTML ────────────────────────────────
     Shows "None" + all charms from products.charms.
  ───────────────────────────────────────────────────────── */
  function buildCharmRow(slotId, selectedCharmVariantId) {
    var noneOpt = [{ name: 'No charm', variantId: 0, price: 0, image: null }];
    var opts = noneOpt.concat(products.charms || []);

    return opts.map(function (charm) {
      var isSel    = charm.variantId === selectedCharmVariantId;
      var classes  = 'bridal-charm-btn' + (isSel ? ' bridal-charm-btn--selected' : '');
      var priceLabel = charm.price > 0 ? '+' + fmtAUD(charm.price) : '';
      var shortName  = charm.name === 'No charm' ? 'None' : charm.name.replace(' Charm', '');

      var imgHtml = charm.image
        ? '<img class="bridal-charm-btn__img" src="' + esc(charm.image) + '" alt="" loading="lazy">'
        : '<span class="bridal-charm-btn__img bridal-charm-btn__img--none" aria-hidden="true">—</span>';

      return '<button'
        + ' class="' + classes + '"'
        + ' data-charm-variant="' + charm.variantId + '"'
        + ' data-charm-price="' + (charm.price || 0) + '"'
        + ' data-slot="' + esc(slotId) + '"'
        + ' title="' + esc(charm.name) + (priceLabel ? ' (' + priceLabel + ')' : '') + '"'
        + ' aria-label="' + esc(charm.name) + (priceLabel ? ', ' + priceLabel : '') + '">'
        + imgHtml
        + '<span class="bridal-charm-btn__label">' + esc(shortName) + '</span>'
        + (priceLabel ? '<span class="bridal-charm-btn__price">' + priceLabel + '</span>' : '')
        + '</button>';
    }).join('');
  }

  /* ── BUILD SWATCH ROW HTML ────────────────────────────────
     Uses product.image (URL from Liquid image_url filter).
     Falls back to a coloured div if no image is available.
  ───────────────────────────────────────────────────────── */
  function buildSwatchRow(slotId, selectedColour) {
    return products.pouches.map(function (p) {
      var isSel  = p.colour === selectedColour;
      var isOut  = !p.available;
      var imgSrc = p.image || '';

      var classes = 'bridal-swatch-btn'
        + (isSel  ? ' bridal-swatch-btn--selected' : '')
        + (isOut  ? ' bridal-swatch-btn--soldout'  : '');

      var innerStyle = imgSrc
        ? 'background-image: url(' + esc(imgSrc) + '); background-size: cover; background-position: center;'
        : 'background-color: rgba(var(--color-foreground), 0.15);';

      return '<button'
        + ' class="' + classes + '"'
        + ' data-colour="' + esc(p.colour) + '"'
        + ' data-variant-id="' + p.variantId + '"'
        + ' data-slot="' + esc(slotId) + '"'
        + ' aria-label="' + esc(p.name) + (isOut ? ' (sold out)' : '') + '"'
        + ' title="' + esc(p.name) + '"'
        + (isOut ? ' disabled' : '') + '>'
        + '<span class="bridal-swatch-btn__inner" style="' + innerStyle + '" aria-hidden="true"></span>'
        + '</button>';
    }).join('');
  }

  /* ── RENDER A SINGLE MAID/EXTRA SLOT ─────────────────────── */
  function renderSlot(slot) {
    var roleIcon = slot.role === 'extra' ? '🌸' : '💐';
    var pouch    = getPouchByColour(slot.colour);
    var colName  = pouch ? pouch.name : slot.colour;

    return '<div class="bridal-slot" data-slot="' + slot.uid + '" data-role="' + slot.role + '">'
      + '<div class="bridal-slot__header">'
        + '<div class="bridal-slot__role-badge">' + roleIcon + ' ' + esc(slot.label) + '</div>'
        + '<button class="bridal-slot__remove" data-remove-slot="' + slot.uid + '" aria-label="Remove ' + esc(slot.label) + '">&#x2715;</button>'
      + '</div>'
      + '<div class="bridal-slot__body">'
        + '<div class="bridal-slot__colour-pick">'
          + '<p class="bridal-slot__pick-label">Choose colour</p>'
          + '<div class="bridal-slot__swatch-row">' + buildSwatchRow(slot.uid, slot.colour) + '</div>'
          + '<div class="bridal-slot__colour-name" id="' + slot.uid + '-colour-name">' + esc(colName) + '</div>'
        + '</div>'
        + '<div class="bridal-slot__charm-pick">'
          + '<p class="bridal-slot__pick-label">Add a charm <span class="bridal-slot__charm-optional">(optional)</span></p>'
          + '<div class="bridal-slot__charm-row" data-slot="' + slot.uid + '">' + buildCharmRow(slot.uid, slot.charmVariantId) + '</div>'
        + '</div>'
      + '</div>'
      + '</div>';
  }

  /* ── RENDER ALL MAID SLOTS ────────────────────────────────── */
  function renderAllSlots() {
    var container = document.getElementById('bridal-maid-slots');
    if (!container) return;
    container.innerHTML = slots
      .filter(function (s) { return s.role !== 'bride'; })
      .map(renderSlot)
      .join('');
  }

  /* ── RENDER ORDER SUMMARY ─────────────────────────────────── */
  function renderSummary() {
    var itemsEl    = document.getElementById('bridal-summary-items');
    var totalEl    = document.getElementById('bridal-grand-total');
    var shippingEl = document.getElementById('bridal-shipping-note');
    var ctaBtn     = document.getElementById('bridal-add-to-cart');
    if (!itemsEl || !totalEl) return;

    var pouchCents = 0;
    var charmCents = 0;
    var allValid   = slots.length > 0;
    var html = '';

    slots.forEach(function (slot) {
      var pouch = getPouchByColour(slot.colour);
      if (!pouch || !pouch.available) { allValid = false; return; }

      pouchCents += pouch.price;

      var thumbHtml = pouch.image
        ? '<img class="bridal-summary__item-thumb" src="' + esc(pouch.image) + '" alt="' + esc(pouch.name) + '" loading="lazy" width="32" height="32">'
        : '<span class="bridal-summary__item-thumb"></span>';

      html += '<div class="bridal-summary__item">'
        + thumbHtml
        + '<span class="bridal-summary__item-desc">' + esc(slot.label) + ' — ' + esc(pouch.name) + '</span>'
        + '<span class="bridal-summary__item-price">' + fmtAUD(pouch.price) + '</span>'
        + '</div>';

      /* Charm line item */
      if (slot.charmVariantId && slot.charmVariantId !== 0) {
        var charm = (products.charms || []).find(function (c) { return c.variantId === slot.charmVariantId; });
        if (charm) {
          charmCents += charm.price;
          var charmThumb = charm.image
            ? '<img class="bridal-summary__item-thumb" src="' + esc(charm.image) + '" alt="" width="32" height="32" loading="lazy">'
            : '<span class="bridal-summary__item-thumb"></span>';
          html += '<div class="bridal-summary__item bridal-summary__item--charm">'
            + charmThumb
            + '<span class="bridal-summary__item-desc bridal-summary__item-desc--charm">↳ ' + esc(charm.name) + '</span>'
            + '<span class="bridal-summary__item-price">' + fmtAUD(charm.price) + '</span>'
            + '</div>';
        }
      }
    });

    itemsEl.innerHTML = html
      || '<div class="bridal-summary__empty">Add people above to build your order.</div>';

    /* ── DISCOUNT CALCULATION ── */
    var totalCents  = pouchCents + charmCents;
    var discountPct = getDiscountPct(slots.length);
    var rawFinal    = discountPct > 0 ? totalCents - Math.round(pouchCents * discountPct / 100) : totalCents;
    var finalCents  = discountPct > 0 ? Math.ceil(rawFinal / 100) * 100 : totalCents;
    var savingsCents = totalCents - finalCents;

    totalEl.textContent = fmtAUD(finalCents);

    /* Savings row (V4 only — element absent in V1–V3) */
    var savingsEl = document.getElementById('bridal-savings-row');
    if (savingsEl) {
      if (savingsCents > 0) {
        savingsEl.innerHTML = '<span>Bundle discount'
          + '<span class="bridal-summary__discount-badge">' + discountPct + '% off</span>'
          + '</span>'
          + '<span class="bridal-summary__savings-amount">−' + fmtAUD(savingsCents) + '</span>';
        savingsEl.style.display = 'flex';
      } else {
        savingsEl.innerHTML = '';
        savingsEl.style.display = 'none';
      }
    }

    /* "Normally" row with save badge + strikethrough price (V4 only) */
    var normallyRow = document.getElementById('bridal-normally-row');
    if (normallyRow) {
      var priceDivider    = document.getElementById('bridal-price-divider');
      var saveBadgeEl     = document.getElementById('bridal-save-badge');
      var originalPriceEl = document.getElementById('bridal-original-price');
      if (savingsCents > 0) {
        if (saveBadgeEl)     saveBadgeEl.textContent     = 'You save ' + fmtAUD(savingsCents);
        if (originalPriceEl) originalPriceEl.textContent = fmtAUD(totalCents);
        normallyRow.style.display = 'flex';
        if (priceDivider) priceDivider.style.display = 'block';
      } else {
        normallyRow.style.display = 'none';
        if (priceDivider) priceDivider.style.display = 'none';
      }
    }

    /* Discount progress nudge (V4 only) */
    var discountProgressEl = document.getElementById('bridal-discount-progress');
    if (discountProgressEl) {
      var nextTier = getNextDiscountTier(slots.length);
      if (slots.length === 0) {
        discountProgressEl.innerHTML = '';
        discountProgressEl.className = 'bridal-discount-progress';
      } else if (nextTier) {
        var needed = nextTier.minQty - slots.length;
        discountProgressEl.innerHTML = 'Add ' + needed + ' more pouch' + (needed > 1 ? 'es' : '')
          + ' to unlock ' + nextTier.pct + '% off';
        discountProgressEl.className = 'bridal-discount-progress';
      } else if (discountPct > 0) {
        discountProgressEl.innerHTML = '<span class="bridal-note-icon">🎁</span>'
          + '<span class="bridal-note-text"><span class="bridal-note-title">Best deal unlocked!</span><span class="bridal-note-subtitle">' + discountPct + '% discount applied</span></span>'
          + '<span class="bridal-note-badge">Bundle</span>';
        discountProgressEl.className = 'bridal-discount-progress bridal-discount-progress--max';
      } else {
        discountProgressEl.innerHTML = '';
        discountProgressEl.className = 'bridal-discount-progress';
      }
    }

    /* Shipping progress */
    var totalAUD = totalCents / 100;
    var pct      = Math.min(100, Math.round((totalAUD / FREE_SHIP_AUD) * 100));

    if (shippingEl) {
      if (totalAUD === 0) {
        shippingEl.innerHTML = '';
        shippingEl.className = 'bridal-summary__shipping';
      } else if (totalAUD >= FREE_SHIP_AUD) {
        shippingEl.innerHTML = '<span class="bridal-note-icon">🎉</span>'
          + '<span class="bridal-note-text"><span class="bridal-note-title">Free shipping unlocked!</span><span class="bridal-note-subtitle">Free standard shipping</span></span>'
          + '<span class="bridal-note-badge">Free</span>';
        shippingEl.className = 'bridal-summary__shipping bridal-summary__shipping--free';
      } else {
        var remaining = (FREE_SHIP_AUD - totalAUD).toFixed(0);
        shippingEl.innerHTML = 'Add $' + remaining + ' more for free shipping'
          + '<div class="bridal-summary__progress-wrap" role="progressbar" aria-valuenow="' + pct + '" aria-valuemin="0" aria-valuemax="100" aria-label="Free shipping progress">'
          + '<div class="bridal-summary__progress-bar" style="width:' + pct + '%"></div>'
          + '</div>';
        shippingEl.className = 'bridal-summary__shipping';
      }
    }

    if (ctaBtn) ctaBtn.disabled = (totalCents === 0 || !allValid);

    /* Sync sticky bar (V3/V4) — no-ops on V1/V2 where these elements don't exist */
    var stickyBar = document.getElementById('bridal-sticky-bar');
    if (stickyBar) {
      var stickyCountEl = document.getElementById('bridal-sticky-count');
      var stickyTotalEl = document.getElementById('bridal-sticky-total');
      var stickyCta     = document.getElementById('bridal-sticky-cta');
      if (stickyCountEl) stickyCountEl.textContent = slots.length + (slots.length === 1 ? ' item' : ' items');
      if (stickyTotalEl) stickyTotalEl.textContent = fmtAUD(finalCents);
      if (stickyCta)     stickyCta.disabled = (totalCents === 0 || !allValid);
      stickyBar.setAttribute('aria-hidden', totalCents > 0 ? 'false' : 'true');
      stickyBar.classList.toggle('bridal-sticky-bar--visible', totalCents > 0);
      var stickyIncentiveEl = document.getElementById('bridal-sticky-incentive');
      if (stickyIncentiveEl) {
        var freeThreshCents = (products.freeShippingThreshold || 75) * 100;
        var hasFreeShip = finalCents >= freeThreshCents;
        var nextTier    = getNextDiscountTier(slots.length);
        var incentive   = '';
        if (slots.length > 0) {
          if (discountPct > 0 && hasFreeShip) {
            incentive = discountPct + '% off · Free shipping unlocked';
          } else if (discountPct > 0 && nextTier) {
            incentive = discountPct + '% off · Add ' + (nextTier.minQty - slots.length) + ' more for ' + nextTier.pct + '% off';
          } else if (discountPct > 0) {
            incentive = discountPct + '% discount applied';
          } else if (nextTier) {
            incentive = 'Add ' + (nextTier.minQty - slots.length) + ' more pouch' + (nextTier.minQty - slots.length > 1 ? 'es' : '') + ' for ' + nextTier.pct + '% off';
          }
        }
        stickyIncentiveEl.textContent = incentive;
      }
    }
  }

  /* ── ADD SLOT ─────────────────────────────────────────────── */
  function addSlot(role, defaultColour) {
    slotUid++;
    var pouch = products.pouches.find(function (p) {
      return p.colour === defaultColour && p.available;
    }) || products.pouches.find(function (p) { return p.available; });

    slots.push({
      uid:            'slot-' + slotUid,
      role:           role,
      label:          '',               // filled by renumberSlots()
      colour:         pouch ? pouch.colour : (defaultColour || 'champagne'),
      variantId:      pouch ? pouch.variantId : 0,
      charmVariantId: 0,
      charmPrice:     0
    });

    renumberSlots();   // ← assigns correct label numbers
    renderAllSlots();
    renderSummary();
    attachSlotEvents();
  }

  /* ── REMOVE SLOT ──────────────────────────────────────────── */
  function removeSlot(uid) {
    slots = slots.filter(function (s) { return s.uid !== uid; });
    renumberSlots();   // ← resequences remaining slots
    renderAllSlots();
    renderSummary();
    attachSlotEvents();
  }

  /* ── EVENT DELEGATION ─────────────────────────────────────── */
  function attachSlotEvents() {
    /* Swatch clicks */
    document.querySelectorAll('.bridal-swatch-btn:not([disabled])').forEach(function (btn) {
      btn.onclick = function (e) {
        e.preventDefault();
        var slotUid   = btn.getAttribute('data-slot');
        var colour    = btn.getAttribute('data-colour');
        var variantId = parseInt(btn.getAttribute('data-variant-id'), 10);

        /* Update bride slot (uid is 'bride-0') or maid slot */
        var slot = slotUid === 'bride-0'
          ? slots.find(function (s) { return s.uid === 'bride-0'; })
          : slots.find(function (s) { return s.uid === slotUid; });

        if (!slot) return;
        slot.colour    = colour;
        slot.variantId = variantId;

        /* Update selected ring */
        var row = btn.closest('.bridal-slot__swatch-row');
        if (row) {
          row.querySelectorAll('.bridal-swatch-btn').forEach(function (b) {
            b.classList.remove('bridal-swatch-btn--selected');
          });
        }
        btn.classList.add('bridal-swatch-btn--selected');

        /* Update colour name text */
        var nameEl = document.getElementById(slotUid + '-colour-name');
        var pouch  = getPouchByColour(colour);
        if (nameEl && pouch) nameEl.textContent = pouch.name;

        renderSummary();
      };
    });

    /* Charm clicks */
    document.querySelectorAll('.bridal-charm-btn').forEach(function (btn) {
      btn.onclick = function (e) {
        e.preventDefault();
        var slotId    = btn.getAttribute('data-slot');
        var variantId = parseInt(btn.getAttribute('data-charm-variant'), 10);
        var price     = parseInt(btn.getAttribute('data-charm-price'), 10) || 0;

        var slot = slots.find(function (s) { return s.uid === slotId; });
        if (!slot) return;
        slot.charmVariantId = variantId;
        slot.charmPrice     = price;

        var row = btn.closest('.bridal-slot__charm-row');
        if (row) {
          row.querySelectorAll('.bridal-charm-btn').forEach(function (b) {
            b.classList.remove('bridal-charm-btn--selected');
          });
        }
        btn.classList.add('bridal-charm-btn--selected');
        renderSummary();
      };
    });

    /* Remove slot buttons */
    document.querySelectorAll('[data-remove-slot]').forEach(function (btn) {
      btn.onclick = function () {
        removeSlot(btn.getAttribute('data-remove-slot'));
      };
    });
  }

  /* ── AJAX ADD TO CART ─────────────────────────────────────── */
  function addAllToCart() {
    var btn       = document.getElementById('bridal-add-to-cart');
    var feedback  = document.getElementById('bridal-atc-feedback');
    var stickyBtn = document.getElementById('bridal-sticky-cta');
    if (!btn || btn.disabled) return;

    /* Deduplicate pouches and charms by variantId */
    var idMap = {};
    slots.forEach(function (slot) {
      if (slot.variantId && slot.variantId !== 0) {
        idMap[slot.variantId] = (idMap[slot.variantId] || 0) + 1;
      }
      if (slot.charmVariantId && slot.charmVariantId !== 0) {
        idMap[slot.charmVariantId] = (idMap[slot.charmVariantId] || 0) + 1;
      }
    });

    var items = Object.keys(idMap)
      .filter(function (id) { return parseInt(id, 10) > 0; })
      .map(function (id) { return { id: parseInt(id, 10), quantity: idMap[id] }; });

    if (!items.length) return;

    btn.disabled    = true;
    btn.textContent = 'Adding to cart\u2026';
    if (stickyBtn) { stickyBtn.disabled = true; stickyBtn.textContent = 'Adding\u2026'; }
    if (feedback) { feedback.textContent = ''; feedback.className = 'bridal-atc-feedback'; }

    fetch('/cart/add.js', {
      method:  'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      /* Request sections in the same call so the HTML reflects the cart state
         AFTER items were added — mirrors Dawn's own add-to-cart approach */
      body:    JSON.stringify({ items: items, sections: ['cart-drawer', 'cart-icon-bubble'] })
    })
    .then(function (res) {
      if (!res.ok) return res.json().then(function (e) { throw new Error(e.description || 'Cart error'); });
      return res.json();
    })
    .then(function (data) {
      btn.textContent = '\u2713 Added to cart!';
      if (stickyBtn) stickyBtn.textContent = '\u2713 Added!';
      if (feedback) {
        feedback.textContent = 'Your bridal order is in the cart. Proceed to checkout when ready.';
        feedback.className   = 'bridal-atc-feedback bridal-atc-feedback--success';
      }

      var cartDrawerEl = document.querySelector('cart-drawer');

      function openDrawerWithSections(sections) {
        if (cartDrawerEl && typeof cartDrawerEl.renderContents === 'function') {
          /* Remove is-empty BEFORE renderContents — page renders <cart-drawer class="is-empty">
             when cart is empty, and open() uses that class to find .drawer__inner-empty for
             focus trapping. Injecting items removes that div, so trapFocus would crash on null. */
          cartDrawerEl.classList.remove('is-empty');
          cartDrawerEl.renderContents({ id: null, sections: sections });
        } else {
          if (sections['cart-drawer'] && cartDrawerEl) {
            var parser     = new DOMParser();
            var freshDoc   = parser.parseFromString(sections['cart-drawer'], 'text/html');
            var freshPanel = freshDoc.querySelector('#CartDrawer');
            var existing   = document.querySelector('#CartDrawer');
            if (freshPanel && existing) {
              cartDrawerEl.classList.remove('is-empty');
              existing.innerHTML = freshPanel.innerHTML;
            }
          }
          if (cartDrawerEl && typeof cartDrawerEl.open === 'function') {
            cartDrawerEl.open();
          } else {
            var cartIcon = document.getElementById('cart-icon-bubble') || document.querySelector('[href="/cart"]');
            if (cartIcon) cartIcon.click();
          }
        }
      }

      if (data.sections) {
        openDrawerWithSections(data.sections);
      } else {
        fetch('/?sections=cart-drawer,cart-icon-bubble')
          .then(function (r) { return r.json(); })
          .then(openDrawerWithSections)
          .catch(function () {
            if (cartDrawerEl && typeof cartDrawerEl.open === 'function') cartDrawerEl.open();
          });
      }

      setTimeout(function () {
        btn.disabled    = false;
        btn.textContent = 'Add All to Cart \u2192';
        if (stickyBtn) { stickyBtn.disabled = false; stickyBtn.textContent = 'Add All to Cart \u2192'; }
      }, 4000);
    })
    .catch(function (err) {
      btn.disabled    = false;
      btn.textContent = 'Add All to Cart \u2192';
      if (stickyBtn) { stickyBtn.disabled = false; stickyBtn.textContent = 'Add All to Cart \u2192'; }
      if (feedback) {
        feedback.textContent = 'Something went wrong: ' + err.message + '. Please try again.';
        feedback.className   = 'bridal-atc-feedback bridal-atc-feedback--error';
      }
    });
  }

  /* ── INIT BRIDE SLOT ──────────────────────────────────────── */
  function initBrideSlot() {
    var champagne = products.pouches.find(function (p) { return p.colour === 'champagne'; });

    /* Add bride to slots array with a fixed uid */
    slots.push({
      uid:            'bride-0',
      role:           'bride',
      label:          'The Bride',
      colour:         'champagne',
      variantId:      champagne ? champagne.variantId : 0,
      charmVariantId: 0,
      charmPrice:     0
    });

    /* Populate the static bride slot's swatch and charm rows */
    var brideSwatchRow = document.querySelector('[data-slot="bride-0"] .bridal-slot__swatch-row');
    if (brideSwatchRow) brideSwatchRow.innerHTML = buildSwatchRow('bride-0', 'champagne');

    var brideCharmRow = document.querySelector('.bridal-slot__charm-row[data-slot="bride-0"]');
    if (brideCharmRow) brideCharmRow.innerHTML = buildCharmRow('bride-0', 0);

    /* Default 3 bridesmaids */
    addSlot('maid', 'champagne');
    addSlot('maid', 'champagne');
    addSlot('maid', 'champagne');

    renderSummary();
    attachSlotEvents();
  }

  /* ── ADD BUTTONS ──────────────────────────────────────────── */
  function initAddButtons() {
    var addMaidBtn  = document.getElementById('bridal-add-maid');
    var addExtraBtn = document.getElementById('bridal-add-extra');

    if (addMaidBtn) {
      addMaidBtn.onclick = function () { addSlot('maid', 'champagne'); };
    }
    if (addExtraBtn) {
      addExtraBtn.onclick = function () { addSlot('extra', 'champagne'); };
    }
  }

  /* ── FAQ ACCORDION ────────────────────────────────────────── */
  function initFaq() {
    document.querySelectorAll('.bridal-faq__item').forEach(function (item) {
      var btn = item.querySelector('.bridal-faq__q');
      var ans = item.querySelector('.bridal-faq__a');
      if (!btn || !ans) return;
      btn.onclick = function () {
        var isOpen = !ans.hasAttribute('hidden');
        document.querySelectorAll('.bridal-faq__item').forEach(function (i) {
          var a = i.querySelector('.bridal-faq__a');
          var b = i.querySelector('.bridal-faq__q');
          if (a) a.setAttribute('hidden', '');
          if (b) b.setAttribute('aria-expanded', 'false');
          i.classList.remove('bridal-faq__item--open');
        });
        if (!isOpen) {
          ans.removeAttribute('hidden');
          btn.setAttribute('aria-expanded', 'true');
          item.classList.add('bridal-faq__item--open');
        }
      };
    });
  }

  /* ── SCROLL ANIMATIONS ────────────────────────────────────── */
  function initScrollAnimations() {
    var targets = document.querySelectorAll(
      '.bridal-step, .bridal-reason, .bridal-review, .bridal-bundle, .bridal-palette__item'
    );
    if (!targets.length || !window.IntersectionObserver) return;
    targets.forEach(function (el) { el.classList.add('bridal-animate'); });
    var obs = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) { e.target.classList.add('is-visible'); obs.unobserve(e.target); }
      });
    }, { threshold: 0.1 });
    targets.forEach(function (el) { obs.observe(el); });
  }

  /* ── SMOOTH SCROLL ────────────────────────────────────────── */
  function initSmoothScroll() {
    document.querySelectorAll('a[href^="#"]').forEach(function (a) {
      a.addEventListener('click', function (e) {
        var target = document.querySelector(this.getAttribute('href'));
        if (!target) return;
        e.preventDefault();
        window.scrollTo({
          top: target.getBoundingClientRect().top + window.pageYOffset - 80,
          behavior: 'smooth'
        });
      });
    });
  }

  /* ── INIT ─────────────────────────────────────────────────── */
  function init() {
    if (!window.BRIDAL_PRODUCTS) {
      console.warn('Pout Pouch: BRIDAL_PRODUCTS not found — cart builder disabled.');
      return;
    }

    products = window.BRIDAL_PRODUCTS;

    /* Read free shipping threshold from Liquid-injected value */
    if (products.freeShippingThreshold) {
      FREE_SHIP_AUD = products.freeShippingThreshold;
    }

    var atcBtn = document.getElementById('bridal-add-to-cart');
    if (atcBtn) atcBtn.addEventListener('click', addAllToCart);

    /* Sticky bar CTA (V3 only — element absent in V1/V2) */
    var stickyCta = document.getElementById('bridal-sticky-cta');
    if (stickyCta) stickyCta.addEventListener('click', addAllToCart);

    /* Hide sticky bar when the main CTA scrolls into view */
    var stickyBarEl = document.getElementById('bridal-sticky-bar');
    if (atcBtn && stickyBarEl && window.IntersectionObserver) {
      new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          stickyBarEl.classList.toggle('bridal-sticky-bar--cta-visible', e.isIntersecting);
        });
      }).observe(atcBtn);
    }

    initBrideSlot();
    initAddButtons();
    initFaq();
    initScrollAnimations();
    initSmoothScroll();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
